import { useState } from 'react';
import { cn, formatCurrency } from '../../lib/utils';

/**
 * TicketSelector - Lucky Number Design System
 *
 * Quantity picker for raffle ticket purchase.
 * Shows price per ticket and total cost.
 * Features lucky number display with accent colors.
 */

const MIN_TICKETS = 1;
const MAX_TICKETS = 10;

export default function TicketSelector({
  ticketPrice,
  ticketsRemaining,
  initialCount = 1,
  onChange,
  disabled = false,
  className,
  light = false, // For use on cream backgrounds
}) {
  const [count, setCount] = useState(initialCount);

  // Calculate max based on remaining tickets
  const maxAllowed = Math.min(MAX_TICKETS, ticketsRemaining);

  const handleChange = (newCount) => {
    const clamped = Math.max(MIN_TICKETS, Math.min(maxAllowed, newCount));
    setCount(clamped);
    onChange?.(clamped);
  };

  const increment = () => handleChange(count + 1);
  const decrement = () => handleChange(count - 1);

  const total = ticketPrice * count;

  return (
    <div className={cn('space-y-5', className)}>
      {/* Quantity selector */}
      <div className="flex items-center justify-between">
        <span className={cn(
          'text-xs font-bold uppercase tracking-wider',
          light ? 'text-midnight/60' : 'text-cream/60'
        )}>
          Number of entries
        </span>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={decrement}
            disabled={disabled || count <= MIN_TICKETS}
            className={cn(
              'w-10 h-10 rounded-full flex items-center justify-center',
              'border-2 transition-all duration-200',
              light
                ? 'border-midnight/20 text-midnight/60 hover:border-petrol hover:text-petrol'
                : 'border-cream/20 text-cream/60 hover:border-accent hover:text-accent',
              'focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2',
              light ? 'focus:ring-offset-cream' : 'focus:ring-offset-midnight',
              'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-current disabled:hover:text-current'
            )}
            aria-label="Decrease quantity"
          >
            <MinusIcon className="w-5 h-5" />
          </button>

          {/* Lucky number display */}
          <div className={cn(
            'lucky-number text-2xl',
            light && 'bg-midnight text-accent'
          )}>
            {count}
          </div>

          <button
            type="button"
            onClick={increment}
            disabled={disabled || count >= maxAllowed}
            className={cn(
              'w-10 h-10 rounded-full flex items-center justify-center',
              'border-2 transition-all duration-200',
              light
                ? 'border-midnight/20 text-midnight/60 hover:border-petrol hover:text-petrol'
                : 'border-cream/20 text-cream/60 hover:border-accent hover:text-accent',
              'focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2',
              light ? 'focus:ring-offset-cream' : 'focus:ring-offset-midnight',
              'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-current disabled:hover:text-current'
            )}
            aria-label="Increase quantity"
          >
            <PlusIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Quick select buttons */}
      <div className="flex gap-2">
        {[1, 3, 5, 10].map((num) => (
          <button
            key={num}
            type="button"
            onClick={() => handleChange(num)}
            disabled={disabled || num > maxAllowed}
            className={cn(
              'flex-1 py-2 px-3 rounded-lg text-sm font-bold font-mono',
              'border-2 transition-all duration-200',
              'focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2',
              light ? 'focus:ring-offset-cream' : 'focus:ring-offset-midnight',
              count === num
                ? light
                  ? 'border-petrol bg-petrol text-cream'
                  : 'border-accent bg-accent/10 text-accent'
                : light
                  ? 'border-midnight/20 text-midnight/60 hover:border-midnight/40'
                  : 'border-cream/20 text-cream/60 hover:border-cream/40',
              'disabled:opacity-40 disabled:cursor-not-allowed'
            )}
          >
            {num}
          </button>
        ))}
      </div>

      {/* Price breakdown */}
      <div className={cn(
        'rounded-xl p-4 space-y-3',
        light ? 'bg-midnight/5' : 'bg-midnight-light'
      )}>
        <div className="flex justify-between text-sm">
          <span className={light ? 'text-midnight/60' : 'text-cream/60'}>
            {count} {count > 1 ? 'entries' : 'entry'} × {formatCurrency(ticketPrice)}
          </span>
          <span className={light ? 'text-midnight' : 'text-cream'}>
            {formatCurrency(total)}
          </span>
        </div>

        <div className={cn(
          'flex justify-between items-center pt-3 border-t',
          light ? 'border-midnight/10' : 'border-cream/10'
        )}>
          <span className={cn(
            'font-bold uppercase text-xs tracking-wider',
            light ? 'text-midnight' : 'text-cream'
          )}>
            Total
          </span>
          <span className="text-xl font-mono font-bold text-accent">
            {formatCurrency(total)}
          </span>
        </div>
      </div>

      {/* Remaining tickets warning */}
      {ticketsRemaining <= 10 && (
        <p className="text-sm text-warning flex items-center gap-2 animate-pulse-urgency">
          <WarningIcon className="w-4 h-4" />
          Only {ticketsRemaining} {ticketsRemaining > 1 ? 'entries' : 'entry'} left!
        </p>
      )}
    </div>
  );
}

/**
 * CompactTicketSelector
 *
 * Inline version for smaller spaces.
 */
export function CompactTicketSelector({
  ticketPrice,
  ticketsRemaining,
  count,
  onChange,
  disabled = false,
  light = false,
}) {
  const maxAllowed = Math.min(MAX_TICKETS, ticketsRemaining);

  return (
    <div className="flex items-center gap-4">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onChange(Math.max(MIN_TICKETS, count - 1))}
          disabled={disabled || count <= MIN_TICKETS}
          className={cn(
            'w-8 h-8 rounded-full border-2 flex items-center justify-center',
            'transition-colors duration-200',
            light
              ? 'border-midnight/20 text-midnight/60 hover:border-petrol hover:text-petrol'
              : 'border-cream/20 text-cream/60 hover:border-accent hover:text-accent',
            'disabled:opacity-40 disabled:cursor-not-allowed'
          )}
        >
          <MinusIcon className="w-4 h-4" />
        </button>
        <div className={cn(
          'lucky-number text-base',
          light && 'bg-midnight text-accent'
        )}>
          {count}
        </div>
        <button
          type="button"
          onClick={() => onChange(Math.min(maxAllowed, count + 1))}
          disabled={disabled || count >= maxAllowed}
          className={cn(
            'w-8 h-8 rounded-full border-2 flex items-center justify-center',
            'transition-colors duration-200',
            light
              ? 'border-midnight/20 text-midnight/60 hover:border-petrol hover:text-petrol'
              : 'border-cream/20 text-cream/60 hover:border-accent hover:text-accent',
            'disabled:opacity-40 disabled:cursor-not-allowed'
          )}
        >
          <PlusIcon className="w-4 h-4" />
        </button>
      </div>
      <span className="text-lg font-mono font-bold text-accent">
        {formatCurrency(ticketPrice * count)}
      </span>
    </div>
  );
}

// Icons
function MinusIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
    </svg>
  );
}

function PlusIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );
}

function WarningIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  );
}
